import { QuoteChatWorkspaceProvider } from "../QuoteChatWorkspaceContext";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, type Location } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  quoteJourneyScope,
  canonicalQuoteOrigin,
  safeSettingsJourneyTarget,
  safeJourneyTarget,
  type JourneyPage,
  type JourneyPageKey,
} from "./quoteJourneyState";
import { validQuoteJourneyViews } from "./quoteJourneyViews";
import { createQuoteJourneyStore } from "./quoteJourneyStore";
import type {
  JourneyRepository,
  JourneyStorage,
} from "./quoteJourneyPersistence";
import { useQuoteJourneyController } from "./useQuoteJourneyController";
import {
  QuoteJourneyContext,
  type QuoteJourneyAccess,
  type JourneyBlockerOptions,
} from "./quoteJourneyContext";
import {
  createJourneyLeave,
  type JourneyDiscardPrompt,
  type JourneyEditor,
  type JourneyLeaveChoice,
} from "./quoteJourneyLeave";
import { QuoteJourneyLeaveDialog } from "./QuoteJourneyLeaveDialog";
import { QuoteJourneyNavigation } from "./QuoteJourneyNavigation";
import { registerProjectContextOwner } from "@/stores/contextNavigation";
import "./QuoteJourneyWorkspace.css";
type Props = {
  children: (location: Location) => ReactNode;
  store?: JourneyRepository;
  storage?: JourneyStorage;
  validateSnapshot?: (
    snapshot: import("./quoteJourneyState").JourneySnapshot,
  ) => boolean;
};
const tabStorage: JourneyStorage = {
  getItem: (key) => window.sessionStorage.getItem(key),
  setItem: (key, value) => window.sessionStorage.setItem(key, value),
  removeItem: (key) => window.sessionStorage.removeItem(key),
};
export function QuoteJourneyWorkspace(props: Props) {
  const { user } = useAuth();
  const location = useLocation();
  const scope =
    user?.id && user.tenant?.id
      ? quoteJourneyScope(user.tenant.id, user.id)
      : null;
  const identity =
    user?.id && user.tenant?.id
      ? { tenantId: user.tenant.id, userId: user.id, role: user.role }
      : null;
  return (
    <QuoteChatWorkspaceProvider identity={identity}>
      {!scope ? (
        props.children(location)
      ) : (
        <WorkspaceBody
          key={JSON.stringify([scope, user?.role])}
          {...props}
          scope={scope}
        />
      )}
    </QuoteChatWorkspaceProvider>
  );
}
function WorkspaceBody({
  children,
  scope,
  store: provided,
  storage = tabStorage,
  validateSnapshot = validQuoteJourneyViews,
}: Props & { scope: string }) {
  const location = useLocation();
  const repository = useMemo(
    () => provided ?? createQuoteJourneyStore(),
    [provided],
  );
  const activeSurface = useRef<"origin" | "settings">("origin");
  const blockers = useRef(
    new Map<string, { reason: () => string | null; settingsOnly?: boolean }>(),
  );
  const editors = useRef(new Map<string, JourneyEditor>());
  const [blocked, setBlocked] = useState<string | null>(null);
  const opener = useRef<HTMLElement | null>(null),
    originRoot = useRef<HTMLDivElement>(null);
  const [prompt, setPrompt] = useState<{
      labels: string[];
      discardOnly: JourneyDiscardPrompt | null;
    } | null>(null),
    [promptBusy, setPromptBusy] = useState(false);
  const choice = useRef<((choice: JourneyLeaveChoice) => void) | null>(null);
  const currentLocation = useRef(location);
  currentLocation.current = location;
  const permit = useRef<{ fromKey: string; target: string } | null>(null);
  const leave = useRef<ReturnType<typeof createJourneyLeave> | null>(null);
  /** keepsWork：本次离开会把原页暂存或停放（去报价设置），keptAcrossSettings 的项不算会丢。 */
  const currentEditors = useCallback(
    (ids?: readonly string[], keepsWork = false) =>
      [...editors.current.entries()]
        .filter(
          ([key, editor]) =>
            key.startsWith(`${activeSurface.current}:`) &&
            (ids === undefined ||
              ids.includes(key.slice(activeSurface.current.length + 1))) &&
            !(keepsWork && editor.keptAcrossSettings),
        )
        .map(([, editor]) => editor),
    [],
  );
  const problem = useCallback((target?: string) => {
    for (const [key, entry] of blockers.current) {
      if (
        !key.startsWith(`${activeSurface.current}:`) ||
        (entry.settingsOnly && !safeJourneyTarget(target))
      )
        continue;
      try {
        const reason = entry.reason();
        if (reason) return reason;
      } catch {
        return "当前页面还没准备好，请稍后再试";
      }
    }
    return null;
  }, []);
  const ask = useCallback(
    (labels: string[], discardOnly?: JourneyDiscardPrompt) =>
      new Promise<JourneyLeaveChoice>((resolve) => {
        choice.current = resolve;
        setPrompt({ labels, discardOnly: discardOnly ?? null });
        setPromptBusy(false);
      }),
    [],
  );
  useLayoutEffect(() => {
    let mounted = true;
    const coordinator = createJourneyLeave({
      currentKey: () => currentLocation.current.key,
      problem,
      editors: currentEditors,
      ask,
      notice: setBlocked,
      finished: () => {
        if (mounted) {
          setPrompt(null);
          setPromptBusy(false);
        }
      },
    });
    leave.current = coordinator;
    return () => {
      mounted = false;
      coordinator.dispose();
      if (leave.current === coordinator) leave.current = null;
      choice.current?.("stay");
      choice.current = null;
      permit.current = null;
    };
  }, [problem, currentEditors, ask]);
  const requestLeave = useCallback(
    (target?: string, ids?: readonly string[], keepsWork = false) =>
      leave.current?.request(target, ids, keepsWork) ?? Promise.resolve(false),
    [],
  );
  // 显式动作：去设置一定先暂存（或沿用当前暂存）原页；清空当前工作的目标是原页地址，不算暂存。
  const beforeNavigate = useCallback(
    async (target: string) => {
      if (
        activeSurface.current === "origin" &&
        document.activeElement instanceof HTMLElement
      )
        opener.current = document.activeElement;
      return requestLeave(
        target,
        undefined,
        activeSurface.current === "origin" &&
          Boolean(safeJourneyTarget(target)) &&
          (!controllerRef.current.originHref ||
            canonicalQuoteOrigin(target) !==
              canonicalQuoteOrigin(controllerRef.current.originHref)),
      );
    },
    [requestLeave],
  );
  // 路由/后退/前进：只有回到当前暂存的设置页（目标历史项带当前活动暂存的 id）时原页会停放。
  const routeKeepsWork = useCallback(
    (target?: string, journeyId?: string | null) =>
      Boolean(
        activeSurface.current === "origin" &&
        target &&
        safeJourneyTarget(target) &&
        canonicalQuoteOrigin(target) !==
          canonicalQuoteOrigin(controllerRef.current.originHref) &&
        journeyId &&
        journeyId === controllerRef.current.journeyId,
      ),
    [],
  );
  const beforeRouteCommit = useCallback((target: string) => {
    permit.current = { fromKey: currentLocation.current.key, target };
  }, []);
  const consumePermit = useCallback((from: Location, target: string) => {
    const allowed = permit.current;
    permit.current = null;
    return allowed?.fromKey === from.key && allowed.target === target;
  }, []);
  const choose = useCallback((value: JourneyLeaveChoice) => {
    const resolve = choice.current;
    if (!resolve) return;
    choice.current = null;
    setPromptBusy(value !== "stay");
    resolve(value);
  }, []);
  const controller = useQuoteJourneyController(scope, {
    store: repository,
    storage,
    beforeNavigate,
    beforeRouteCommit,
    validateSnapshot,
  });
  const controllerRef = useRef(controller);
  controllerRef.current = controller;
  const overlay = Boolean(controller.background);
  activeSurface.current =
    overlay ||
    safeSettingsJourneyTarget(
      location.pathname + location.search + location.hash,
    )
      ? "settings"
      : "origin";
  const consumed = useRef(new Map<JourneyPageKey, JourneyPage>());
  const restoredPage = useCallback((key: JourneyPageKey) => {
    const page = controllerRef.current.restoredPage(key);
    return page && consumed.current.get(key) !== page ? page : null;
  }, []);
  const consumePage = useCallback((key: JourneyPageKey) => {
    const page = controllerRef.current.restoredPage(key);
    if (page) consumed.current.set(key, page);
  }, []);
  const registerBlocker = useCallback(
    (
      surface: string,
      id: string,
      reason: () => string | null,
      options?: JourneyBlockerOptions,
    ) => {
      const key = `${surface}:${id}`,
        entry = { reason, settingsOnly: options?.settingsOnly };
      blockers.current.set(key, entry);
      return () => {
        if (blockers.current.get(key) === entry) blockers.current.delete(key);
      };
    },
    [],
  );
  const originBlocker = useCallback(
    (
      id: string,
      reason: () => string | null,
      options?: JourneyBlockerOptions,
    ) => registerBlocker("origin", id, reason, options),
    [registerBlocker],
  );
  const settingsBlocker = useCallback(
    (
      id: string,
      reason: () => string | null,
      options?: JourneyBlockerOptions,
    ) => registerBlocker("settings", id, reason, options),
    [registerBlocker],
  );
  const registerEditor = useCallback(
    (surface: string, id: string, editor: JourneyEditor) => {
      const key = `${surface}:${id}`;
      editors.current.set(key, editor);
      return () => {
        if (editors.current.get(key) === editor) editors.current.delete(key);
      };
    },
    [],
  );
  const originEditor = useCallback(
    (id: string, editor: JourneyEditor) => registerEditor("origin", id, editor),
    [registerEditor],
  );
  const settingsEditor = useCallback(
    (id: string, editor: JourneyEditor) =>
      registerEditor("settings", id, editor),
    [registerEditor],
  );
  const originEditorLeave = useCallback(
    (id: string | readonly string[]) =>
      activeSurface.current === "origin"
        ? requestLeave(undefined, typeof id === "string" ? [id] : id)
        : Promise.resolve(false),
    [requestLeave],
  );
  const settingsEditorLeave = useCallback(
    (id: string | readonly string[]) =>
      activeSurface.current === "settings"
        ? requestLeave(undefined, typeof id === "string" ? [id] : id)
        : Promise.resolve(false),
    [requestLeave],
  );
  // 停放也要先拦下来：keptAcrossSettings 的项不用问，但要等暂存写进最新内容再走（见 requestRouteLeave）。
  const hasPending = useCallback(
    (target?: string) => {
      try {
        return (
          controllerRef.current.busy ||
          Boolean(problem(target)) ||
          currentEditors().some((e) => e.dirty())
        );
      } catch {
        return true;
      }
    },
    [problem, currentEditors],
  );
  useLayoutEffect(
    () =>
      registerProjectContextOwner({
        pending: () => hasPending(),
        prepare: async () => {
          if (controllerRef.current.busy) {
            setBlocked("正在保存原工作，请稍后再试");
            return null;
          }
          return leave.current?.prepare() ?? null;
        },
      }),
    [hasPending],
  );
  const requestRouteLeave = useCallback(
    async (target: string, journeyId?: string | null) => {
      if (controllerRef.current.busy) {
        setBlocked("正在保存原工作，请稍后再试");
        return false;
      }
      const keepsWork = routeKeepsWork(target, journeyId);
      if (!(await requestLeave(target, undefined, keepsWork))) return false;
      // 原页停放，但暂存是当初去设置那一刻的；停放期间才有的、只能随暂存保留的内容
      // （如后退回来后才传的文件）先写进同一份暂存，否则刷新/关页后恢复出来就没了。
      if (
        keepsWork &&
        currentEditors().some((e) => e.keptAcrossSettings && e.dirty())
      )
        return controllerRef.current.refreshStash();
      return true;
    },
    [requestLeave, routeKeepsWork, currentEditors],
  );
  const clearCurrentWork = useCallback(async () => {
    const cleared = await controllerRef.current.clearCurrentWork();
    if (cleared) {
      consumed.current.clear();
      opener.current = null;
    }
    return cleared;
  }, []);
  // 提示能关掉(固定在页面顶上,不关会一直挡着顶部按钮);换了页面,上一次拦下的原因也不再挂着
  const [dismissedError, setDismissedError] = useState<string | null>(null);
  useEffect(() => {
    if (controller.error === null) setDismissedError(null);
  }, [controller.error]);
  useEffect(() => {
    setBlocked(null);
  }, [location.key]);
  const notice =
    blocked ?? (controller.error !== dismissedError ? controller.error : null);
  const dismissNotice = () => {
    if (blocked) setBlocked(null);
    else setDismissedError(controller.error);
  };
  const targetCaptures = useRef(
    new Map<JourneyPageKey, { capture: () => JourneyPage }>(),
  );
  const registerTargetPage = useCallback(
    (key: JourneyPageKey, capture: () => JourneyPage) => {
      const entry = { capture };
      targetCaptures.current.set(key, entry);
      return () => {
        if (targetCaptures.current.get(key) === entry)
          targetCaptures.current.delete(key);
      };
    },
    [],
  );
  const originAccess: QuoteJourneyAccess = {
    controller: { ...controller, clearCurrentWork },
    suspended: overlay || controller.busy || prompt !== null,
    restoredPage,
    consumePage,
    registerPage: controller.registerPage,
    registerBlocker: originBlocker,
    registerEditor: originEditor,
    requestEditorLeave: originEditorLeave,
    notice,
  };
  const settingsAccess: QuoteJourneyAccess = {
    ...originAccess,
    registerPage: registerTargetPage,
    restoredPage: () => null,
    consumePage: () => {},
    suspended: controller.busy || prompt !== null,
    registerBlocker: settingsBlocker,
    registerEditor: settingsEditor,
    requestEditorLeave: settingsEditorLeave,
  };
  const wasOverlay = useRef(false);
  useEffect(() => {
    if (wasOverlay.current && !overlay) {
      const target = opener.current;
      if (target?.isConnected && originRoot.current?.contains(target))
        target.focus({ preventScroll: true });
      else originRoot.current?.focus({ preventScroll: true });
    }
    wasOverlay.current = overlay;
  }, [overlay]);
  if (controller.restoring)
    return (
      <div className="qq-journey-loading" role="status">
        正在恢复原报价…
      </div>
    );
  return (
    <>
      <QuoteJourneyNavigation
        consumePermit={consumePermit}
        hasPending={hasPending}
        requestLeave={requestRouteLeave}
      />
      {prompt && (
        <QuoteJourneyLeaveDialog
          labels={prompt.labels}
          discardOnly={prompt.discardOnly}
          busy={promptBusy}
          choose={choose}
        />
      )}
      {notice && (
        <div className="qq-journey-notice" role="alert">
          <span>{notice}</span>
          <button
            type="button"
            className="qq-journey-notice-close"
            aria-label="关闭提示"
            onClick={dismissNotice}
          >
            ×
          </button>
        </div>
      )}
      <QuoteJourneyContext.Provider
        value={
          overlay
            ? originAccess
            : activeSurface.current === "settings"
              ? settingsAccess
              : originAccess
        }
      >
        <div
          ref={originRoot}
          tabIndex={-1}
          className="qq-journey-origin"
          hidden={overlay}
          inert={overlay || controller.busy}
          aria-hidden={overlay || undefined}
          aria-busy={controller.busy || undefined}
        >
          {children(controller.background ?? location)}
        </div>
      </QuoteJourneyContext.Provider>
      {overlay && (
        <QuoteJourneyContext.Provider value={settingsAccess}>
          {children(location)}
        </QuoteJourneyContext.Provider>
      )}
    </>
  );
}
